import React, { useState, useEffect, useCallback } from 'react';
import { leaveAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Plus, CalendarOff, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';

const STATUS_STYLES = {
  pending: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  approved: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  declined: 'bg-red-500/15 text-red-400 border-red-500/20',
  cancelled: 'bg-gray-500/15 text-gray-400 border-gray-500/20',
};

const TYPE_LABELS = {
  annual: 'Annual Leave',
  sick: 'Sick Leave',
  unpaid: 'Unpaid Leave',
  other: 'Other',
};

const fmtDate = (d) => format(parseISO(d), 'EEE d MMM yyyy');

export default function MedicLeave() {
  const [requests, setRequests] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [form, setForm] = useState({ leave_type: 'annual', start_date: '', end_date: '', reason: '' });

  const fetchData = useCallback(async () => {
    try {
      const [reqRes, balRes] = await Promise.all([
        leaveAPI.list(),
        leaveAPI.myBalance().catch(() => ({ data: null })),
      ]);
      setRequests(reqRes.data);
      setBalance(balRes.data);
    } catch (error) {
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const daysRequested =
    form.start_date && form.end_date && form.end_date >= form.start_date
      ? differenceInCalendarDays(parseISO(form.end_date), parseISO(form.start_date)) + 1
      : null;

  const handleSubmit = async () => {
    if (!form.start_date || !form.end_date) {
      toast.error('Please select start and end dates');
      return;
    }
    if (form.end_date < form.start_date) {
      toast.error('End date must be on or after start date');
      return;
    }
    setSubmitting(true);
    try {
      await leaveAPI.create({
        leave_type: form.leave_type,
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason || undefined,
      });
      toast.success('Leave request submitted');
      setShowModal(false);
      setForm({ leave_type: 'annual', start_date: '', end_date: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    setCancellingId(id);
    try {
      await leaveAPI.cancel(id);
      toast.success('Leave request cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel request');
    } finally {
      setCancellingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="medic-leave-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
            <CalendarOff className="w-6 h-6 text-[#1ABC9C]" />
            My Leave
          </h1>
          <p className="text-sm text-[#A1A1AA] mt-1">Request time off and track approvals</p>
        </div>
        <Button
          className="bg-[#1ABC9C] hover:bg-[#16a085] text-white"
          onClick={() => setShowModal(true)}
          data-testid="request-leave-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Request Leave
        </Button>
      </div>

      {balance && (
        <Card className="bg-[#2B2B2B] border-white/5" data-testid="leave-balance-card">
          <CardContent className="p-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <div>
              <div className="text-xs text-[#A1A1AA]">Annual allowance {balance.year}</div>
              <div className="text-lg font-semibold text-[#F2F2F2]">{balance.annual_allowance} days</div>
            </div>
            <div>
              <div className="text-xs text-[#A1A1AA]">Approved</div>
              <div className="text-lg font-semibold text-[#F2F2F2]">{balance.approved_days}</div>
            </div>
            <div>
              <div className="text-xs text-[#A1A1AA]">Pending</div>
              <div className="text-lg font-semibold text-[#FFC04D]">{balance.pending_days}</div>
            </div>
            <div>
              <div className="text-xs text-[#A1A1AA]">Remaining</div>
              <div className="text-lg font-semibold text-[#1ABC9C]" data-testid="leave-balance-remaining">
                {balance.remaining_days} days
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {requests.length === 0 ? (
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-12 text-center text-[#A1A1AA]" data-testid="medic-leave-empty">
            <CalendarOff className="w-10 h-10 mx-auto mb-3 opacity-40" />
            No leave requests yet. Tap "Request Leave" to submit one.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <Card key={req.id} className="bg-[#2B2B2B] border-white/5" data-testid={`medic-leave-card-${req.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[#F2F2F2] font-medium">{TYPE_LABELS[req.leave_type] || req.leave_type}</span>
                      <Badge className={`${STATUS_STYLES[req.status]} border`}>{req.status}</Badge>
                    </div>
                    <p className="text-sm text-[#A1A1AA]">
                      {fmtDate(req.start_date)} → {fmtDate(req.end_date)} · {req.days} day{req.days !== 1 ? 's' : ''}
                    </p>
                    {req.reason && <p className="text-xs text-[#A1A1AA]">Reason: {req.reason}</p>}
                    {req.reviewer_notes && (
                      <p className="text-xs text-[#FFC04D]">Reviewer note: {req.reviewer_notes}</p>
                    )}
                    {req.reviewed_by && (
                      <p className="text-xs text-[#A1A1AA]">Reviewed by {req.reviewed_by}</p>
                    )}
                  </div>
                  {req.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10 shrink-0"
                      disabled={cancellingId === req.id}
                      onClick={() => handleCancel(req.id)}
                      data-testid={`cancel-leave-btn-${req.id}`}
                    >
                      {cancellingId === req.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <X className="w-4 h-4 mr-1" />}
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Request Leave Dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle>Request Leave</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Your request will be sent to the operations team for approval.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs text-[#A1A1AA]">Leave Type</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm(f => ({ ...f, leave_type: v }))}>
                <SelectTrigger className="bg-[#1A1A1A] border-white/10" data-testid="leave-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="annual">Annual Leave</SelectItem>
                  <SelectItem value="sick">Sick Leave</SelectItem>
                  <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-[#A1A1AA]">Start Date</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/10"
                  data-testid="leave-start-date"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-[#A1A1AA]">End Date</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  min={form.start_date || undefined}
                  onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/10"
                  data-testid="leave-end-date"
                />
              </div>
            </div>
            {daysRequested && (
              <p className="text-sm text-[#1ABC9C]" data-testid="leave-days-count">
                {daysRequested} day{daysRequested !== 1 ? 's' : ''} requested
              </p>
            )}
            {daysRequested && balance && form.leave_type === 'annual' && daysRequested > balance.remaining_days && (
              <p className="text-sm text-red-400" data-testid="leave-balance-warning">
                This exceeds your remaining allowance ({balance.remaining_days} day{balance.remaining_days !== 1 ? 's' : ''}). Managers will still see the request.
              </p>
            )}
            <div className="space-y-1">
              <Label className="text-xs text-[#A1A1AA]">Reason (optional)</Label>
              <Textarea
                placeholder="e.g. Family holiday"
                value={form.reason}
                onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
                data-testid="leave-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button
              className="bg-[#1ABC9C] hover:bg-[#16a085] text-white"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="submit-leave-request-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
